import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { portalApi } from '../api/portalClient';

interface PortalUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface PortalCustomer {
  id: string;
  code: string;
  name: string;
}

interface PortalAuthContextType {
  user: PortalUser | null;
  customer: PortalCustomer | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [customer, setCustomer] = useState<PortalCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if we have a token and try to get current user
    const token = portalApi.getToken();
    if (token) {
      portalApi
        .getMe()
        .then((result) => {
          setUser(result.data.user);
          setCustomer(result.data.customer);
        })
        .catch(() => {
          portalApi.setToken(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const result = await portalApi.login(email, password);
    setUser(result.data.user);
    setCustomer(result.data.customer);
  };

  const logout = async () => {
    await portalApi.logout();
    setUser(null);
    setCustomer(null);
  };

  return (
    <PortalAuthContext.Provider
      value={{
        user,
        customer,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const context = useContext(PortalAuthContext);
  if (context === undefined) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return context;
}
